// src/modules/estimates/EstimateList.js
import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate, Link } from 'react-router-dom';
import { FaEdit, FaTrash, FaEye, FaSearch, FaFilter, FaPaperPlane, FaPlus, FaList, FaMapMarkerAlt, FaUser, FaInbox, FaSortAmountDown, FaSortAmountUp, FaHardHat } from 'react-icons/fa';
import { getEstimateAddress, formatAddress } from '../../utils/estimateAddressUtils';
import DashboardLayout from '../../layouts/DashboardLayout';
import Input from '../../components/shared/Input';
import Button from '../../components/shared/Button';
import Select from '../../components/shared/Select';
import Pagination from '../../components/shared/Pagination';
import Modal from '../../components/shared/Modal';
import { getEstimateDetails, deleteEstimate, updateEstimateStatus } from './EstimateService'; // Ensure EstimateService has these functions
import { fetchJobs } from '../jobs/jobsService';
import { formatCurrency } from '../../utils/utils';
import { ResponsiveHeader, StatusBadge } from '../../components/styled/CommonStyles';
import {
  GridContainer,
  Card,
  CardHeader,
  CardContent,
  InfoRow,
  ActionIconsContainer,
  ActionButton,
  EmptyState,
  FilterSection,
  FilterGroup,
  FilterLabel,
  FilterButton
} from '../../components/styled/GridComponents';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import FeedbackModal from '../../components/shared/ModalNotification';
import { useCommunication } from '../communications/context/CommunicationContext';
import { ChangeOrderSummaryBadge } from '../change-orders/ChangeOrderListSection';

const EstimateList = () => {
  const [estimates, setEstimates] = useState([]); // All estimates data
  const [jobsByEstimateId, setJobsByEstimateId] = useState({}); // Map of estimate_id -> job
  const [searchQuery, setSearchQuery] = useState(''); // Search query state
  const [currentPage, setCurrentPage] = useState(1); // Current page for pagination
  const [itemsPerPage] = useState(20); // Number of items per page
  const [sortConfig, setSortConfig] = useState({ field: 'createdAt', direction: 'desc' }); // Default: newest first
  const [statusFilter, setStatusFilter] = useState(''); // Status filter state
  const [serviceFilter, setServiceFilter] = useState([]); // Service filter state (array of service names; [] = all)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false); // State for delete confirmation modal
  const [selectedEstimate, setSelectedEstimate] = useState(null); // Estimate selected for deletion
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSuccess, setFeedbackSuccess] = useState(true);

  const navigate = useNavigate();

  // Fetch all estimates when component mounts
  useEffect(() => {
    const fetchEstimates = async () => {
      try {
        setLoading(true);
        const response = await getEstimateDetails();
        setEstimates(response.data || []); // Ensure estimates is an array
      } catch (error) {
        console.error('Error fetching estimates:', error);
      }finally{
        setLoading(false);
      }
    };
    fetchEstimates();

    const loadJobs = async () => {
      try {
        const jobs = await fetchJobs();
        const map = {};
        (Array.isArray(jobs) ? jobs : []).forEach((job) => {
          if (job.estimate_id) {
            map[job.estimate_id] = job;
          }
        });
        setJobsByEstimateId(map);
      } catch (error) {
        console.error('Error fetching jobs for estimate list:', error);
      }
    };
    loadJobs();
  }, []);


  useEffect(() => {
    setCurrentPage(1); // Reset to the first page when search query or filter changes
  }, [searchQuery, statusFilter, serviceFilter]);

  const availableServiceNames = useMemo(() => {
    const names = new Set();
    estimates.forEach((estimate) => {
      (estimate.items || []).forEach((item) => {
        if (item.Service?.service_name) {
          names.add(item.Service.service_name);
        }
      });
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [estimates]);

  const filteredAndSortedEstimates = useMemo(() => {
    let filtered = estimates;
    if (searchQuery) {
      filtered = filtered.filter(
        (estimate) => {
          const addr = getEstimateAddress(estimate);
          return estimate.Lead.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            estimate.Lead.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (addr.city || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (addr.street || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            estimate.Lead.phone.toLowerCase().includes(searchQuery.toLowerCase());
        }
      );
    }

    if (statusFilter) {
      filtered = filtered.filter((estimate) => estimate.status === statusFilter);
    }

    if (serviceFilter.length > 0) {
      filtered = filtered.filter((estimate) =>
        (estimate.items || []).some((item) =>
          item.Service?.service_name && serviceFilter.includes(item.Service.service_name)
        )
      );
    }

    return [...filtered].sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return sortConfig.direction === 'desc' ? dateB - dateA : dateA - dateB;
    });
  }, [estimates, searchQuery, statusFilter, serviceFilter, sortConfig]);

  const handleDelete = async () => {
    try {
      await deleteEstimate(selectedEstimate.id);
      const updatedEstimates = estimates.filter((estimate) => estimate.id !== selectedEstimate.id);
      setEstimates(updatedEstimates);
      setDeleteModalOpen(false); // Close modal on successful delete
    } catch (error) {
      console.error('Failed to delete estimate:', error);
    }
  };

  const handleUpdateStatus = async (id, newStatus) => {
    try {
      await updateEstimateStatus(id, { newStatus: newStatus });
      const updatedEstimates = estimates.map((estimate) =>
        estimate.id === id ? { ...estimate, status: newStatus } : estimate
      );
      setEstimates(updatedEstimates);
      setSendModalOpen(false);
      setFeedbackMessage('Estimate sent!')
      setFeedbackSuccess(true);
      setShowFeedback(true);

    } catch (error) {
      setFeedbackMessage(`Error sending Estimate ${error}`);
      setFeedbackSuccess(false);
      setShowFeedback(true);
      console.error('Failed to update status:', error);
    }
  };

  // Add a new function to handle sending the estimate via SMS
  const handleSendSms = (estimate) => {
    // Generate the estimate link/message
    const defaultMessage = `Hello ${estimate.Lead.name}, this is Ageu from Mr. Patch Asphalt. We sent you an email with a link to view your estimate online and sign the agreement. Please check your inbox and look for our email: contact@mrpatchasphalt.com. Check the SPAM folder. Let me know if you have any questions.`;
       
    // Close the send modal if it was open
    setSendModalOpen(false);
  };

  // Open the delete confirmation modal
  const openDeleteModal = (estimate) => {
    setSelectedEstimate(estimate);
    setDeleteModalOpen(true);
  };

  // Close the delete confirmation modal
  const closeDeleteModal = () => {
    setSelectedEstimate(null);
    setDeleteModalOpen(false);
  };

  const handleFeedbackComplete = () => {
    setShowFeedback(false);
    // Redirect to job creation if status was changed to Accepted
  };

  const handleSort = () => {
    setSortConfig(prev => ({
      ...prev,
      direction: prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Pagination calculations
  const indexOfLastEstimate = currentPage * itemsPerPage;
  const indexOfFirstEstimate = indexOfLastEstimate - itemsPerPage;
  const currentEstimates = filteredAndSortedEstimates.slice(indexOfFirstEstimate, indexOfLastEstimate);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Draft': return '#666666';
      case 'Sent': return '#3498db';
      case 'Accepted': return '#27ae60';
      case 'Declined': return '#c9595c';
      case 'Expired': return '#c9595c';
      case 'Completed': return '#f69620';
      default: return '#666666';
    }
  };

  const getJobStatusColor = (status) => {
    switch (status) {
      case 'Scheduled': return '#3498db';
      case 'In Progress': return '#f69620';
      case 'Completed': return '#27ae60';
      case 'Cancelled': return '#c9595c';
      default: return '#666666';
    }
  };

  return (
    <DashboardLayout>
      <ResponsiveHeader
        title="Estimates"
        onSearch={setSearchQuery}
        onAdd={() => navigate('/estimates/new')}
      />

      {loading ? (
        <>
      <LoadingSpinner />
        </>
      ) : (
        <>

      <FilterSection>
        <FilterGroup>
          <FilterLabel>Status:</FilterLabel>
          {['All', 'Draft', 'Sent', 'Accepted', 'Declined', 'Expired', 'Viewed', 'Completed'
          ].map(status => (
            <FilterButton
              key={status}
              isActive={statusFilter === (status === 'All' ? '' : status)}
              onClick={() => setStatusFilter(status === 'All' ? '' : status)}
            >
              {status}
            </FilterButton>
          ))}
        </FilterGroup>
        <FilterGroup>
          <FilterLabel>Services:</FilterLabel>
          <FilterButton
            isActive={serviceFilter.length === 0}
            onClick={() => setServiceFilter([])}
          >
            All
          </FilterButton>
          {availableServiceNames.map((name) => {
            const isSelected = serviceFilter.includes(name);
            return (
              <FilterButton
                key={name}
                isActive={isSelected}
                onClick={() =>
                  setServiceFilter((prev) =>
                    isSelected ? prev.filter((n) => n !== name) : [...prev, name]
                  )
                }
              >
                {name}
              </FilterButton>
            );
          })}
        </FilterGroup>
        <FilterGroup>
          <FilterLabel>Sort by:</FilterLabel>
          <FilterButton
            isActive={true}
            onClick={handleSort}
          >
            {sortConfig.direction === 'desc' ? <FaSortAmountDown /> : <FaSortAmountUp />}
            {' '}{sortConfig.direction === 'desc' ? 'Newest First' : 'Oldest First'}
          </FilterButton>
        </FilterGroup>
      </FilterSection>

      <GridContainer>
        {currentEstimates.length === 0 ? (
          <EmptyState>
            <FaInbox size={48} />
            <p>No estimates found. Create a new estimate to get started.</p>
          </EmptyState>
        ) : (
          currentEstimates.map((estimate) => {
            const linkedJob = jobsByEstimateId[estimate.id];
            return (
            <Card key={estimate.id}>
              <CardHeader>
                <h3>{estimate.Lead.name}</h3>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <StatusBadge color={getStatusColor(estimate.status)}>
                    {estimate.status}
                  </StatusBadge>
                  {linkedJob && (
                    <StatusBadge
                      as={Link}
                      to={`/jobs/${linkedJob.job_id}`}
                      color={getJobStatusColor(linkedJob.status)}
                      title={`Job ${linkedJob.status}`}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', textDecoration: 'none' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FaHardHat /> {linkedJob.status}
                    </StatusBadge>
                  )}
                </div>
              </CardHeader>

              <CardContent>
                <InfoRow>
                  <FaUser />
                  <span>#{estimate.id}</span>
                </InfoRow>

                <InfoRow>
                  <FaMapMarkerAlt />
                  <span>{formatAddress(getEstimateAddress(estimate))}</span>
                </InfoRow>

                <InfoRow>
                  <FaList />
                  <span>
                    {estimate.items.map((item, index) => (
                      <span key={item.id}>
                        {item.Service.service_name}
                        {index < estimate.items.length - 1 ? ', ' : ''}
                      </span>
                    ))}
                  </span>
                </InfoRow>

                <InfoRow>
                  <strong>Total: {formatCurrency(estimate.totalPrice)}</strong>
                  <ChangeOrderSummaryBadge changeOrders={estimate.changeOrders} />
                </InfoRow>
              </CardContent>

              <ActionIconsContainer>
                <ActionButton
                  as={Link}
                  to={`/estimates/view/${estimate.id}`}
                  title="View Details"
                >
                  <FaEye />
                </ActionButton>

                <ActionButton
                  as={Link}
                  to={`/estimates/edit/${estimate.id}`}
                  title="Edit Estimate"
                  style={estimate.status === 'Sent' ? { pointerEvents: 'none', opacity: 0.5 } : {}}
                >
                  <FaEdit />
                </ActionButton>
                <ActionButton
                  onClick={() => {
                    setSelectedEstimate(estimate);
                    setSendModalOpen(true);
                  }}
                  title="Send Estimate"
                >
                  <FaPaperPlane />
                </ActionButton>                
                <ActionButton
                  onClick={() => {
                    setSelectedEstimate(estimate);
                    setDeleteModalOpen(true);
                  }}
                  title="Delete Estimate"
                >
                  <FaTrash />
                </ActionButton>
              </ActionIconsContainer>
            </Card>
            );
          })
        )}
      </GridContainer>

      {filteredAndSortedEstimates.length > itemsPerPage && (
        <Pagination
          itemsPerPage={itemsPerPage}
          totalItems={filteredAndSortedEstimates.length}
          paginate={paginate}
          currentPage={currentPage}
        />
      )}
      </>

      )}

      <Modal
        isOpen={deleteModalOpen}
        title="Delete Estimate"
        message={`Are you sure you want to delete the estimate for ${selectedEstimate?.Lead?.name}?`}
        onConfirm={handleDelete}
        onCancel={() => {
          setDeleteModalOpen(false);
          setSelectedEstimate(null);
        }}
      />

      <Modal
        isOpen={sendModalOpen}
        title="Send Estimate"
        message={`Are you sure you want to send this estimate to ${selectedEstimate?.Lead?.name}?`}
        onConfirm={() => handleUpdateStatus(selectedEstimate.id, 'Sent')}
        onCancel={() => {
          setSendModalOpen(false);
          setSelectedEstimate(null);
        }}
      />

    <FeedbackModal
      isOpen={showFeedback}
      message={feedbackMessage}
      success={feedbackSuccess}
      onComplete={handleFeedbackComplete}
    />

    </DashboardLayout>
  );
};

export default EstimateList;